<!--
	One symbology swatch for a legend item derived by `symbology.ts`: a
	pre-rendered PNG sign image, a sprite icon, a line sample (width/dash/colour),
	fill patch (colour/outline/opacity), or circle dot.
-->
<script lang="ts">
	import { PATCH_FALLBACK } from '#lib/vizPalette.js';
	import SpriteIcon from './SpriteIcon.svelte';
	import type { Swatch } from './symbology';

	interface Props {
		swatch: Swatch;
		spritePath?: string;
		/** Compact and muted, for a legend section's one-line caption hints. */
		hint?: boolean;
	}

	const { swatch, spritePath, hint = false }: Props = $props();

	// Dash values are in line-width multiples on the map; scale up for the
	// small sample so the pattern stays visible.
	const dashArray = $derived(swatch.dash?.map((d) => d * 3).join(' '));
	const fillOpacity = $derived(swatch.opacity ?? 1);
</script>

{#if swatch.image}
	<img class="signImg" src={swatch.image} alt="" />
{:else if swatch.kind === 'icon' && swatch.icon && spritePath}
	<SpriteIcon icon={swatch.icon} {spritePath} />
{:else if swatch.kind === 'line'}
	<svg class="swatch" class:hint viewBox="0 0 24 14" preserveAspectRatio="none" aria-hidden="true">
		<line
			x1="1"
			y1="7"
			x2="23"
			y2="7"
			stroke={swatch.color ?? PATCH_FALLBACK}
			stroke-width={Math.min(swatch.width ?? 6, 8)}
			stroke-dasharray={dashArray}
			stroke-opacity={swatch.opacity ?? 1} />
	</svg>
{:else if swatch.kind === 'circle'}
	<div
		class="swatch dot"
		style:background-color={swatch.color ?? PATCH_FALLBACK}
		style:border-color={swatch.outline ?? 'transparent'}>
	</div>
{:else}
	<div
		class="swatch box"
		style:background-color={swatch.color ?? PATCH_FALLBACK}
		style:opacity={fillOpacity}
		style:border-color={swatch.outline ?? swatch.color ?? PATCH_FALLBACK}>
	</div>
{/if}

<style>
	.signImg {
		flex-shrink: 0;
		width: var(--icon-xl);
		height: var(--icon-xl);
	}

	.swatch {
		flex-shrink: 0;
		width: var(--icon-lg);
		height: calc(var(--icon-lg) * 7 / 12);

		&.box {
			border-width: 1px;
			border-style: solid;
		}

		&.dot {
			border: 1px solid;
			border-radius: var(--radius-full);
			width: var(--icon-xs);
			height: var(--icon-xs);
		}

		&.hint {
			width: var(--icon-md);
			height: var(--space-1);

			line {
				stroke: var(--text-muted);
			}
		}
	}
</style>
